import { useEffect, useId, useState, type FocusEvent } from 'react';
import { prefersReducedMotion } from './emjaeMascotMotion';
import { CALLOUT_SHOW_DELAY_MS, CALLOUT_VISIBLE_MS, calloutAlreadySeen, markCalloutSeen } from './badgerCallout';

/**
 * The badger's speech bubble and the poses that go with it, shared by every place the badger
 * lives (the corner launcher and the top-bar one) so the rules exist once.
 *
 * The call-out shows: once per browser session shortly after the first load, with a wave; on
 * hover or keyboard focus of the badger; and, every time, when a reply finishes while the chat
 * is closed (a happy hop and "I have an answer for you"). Opening the chat answers it: the
 * call-out, the wave and the hop are cleared, and the session's call-out counts as used.
 */
export function useBadgerCallout({
  bubbleOpen,
  unreadCount,
}: {
  bubbleOpen: boolean;
  unreadCount: number;
}) {
  const [autoVisible, setAutoVisible] = useState(false);
  const [hover, setHover] = useState(false);
  // Keyboard focus anywhere inside the launcher or its call-out (so Shift+Tab from
  // the launcher to the call-out's X does not unmount the call-out under the cursor).
  const [focusWithin, setFocusWithin] = useState(false);
  const calloutId = useId();
  const [unreadNote, setUnreadNote] = useState(false);
  const [hop, setHop] = useState(false);
  // The wave plays once per call-out; its animationend (or the call-out ending) is the end of it.
  const [waveDone, setWaveDone] = useState(false);

  // Once per session: appear after the delay, hide after the visible time. The
  // key is written when it appears, so a remount in the same session stays quiet.
  useEffect(() => {
    if (bubbleOpen || calloutAlreadySeen()) return undefined;
    let hideTimer: ReturnType<typeof setTimeout> | undefined;
    const showTimer = setTimeout(() => {
      markCalloutSeen();
      setAutoVisible(true);
      hideTimer = setTimeout(() => setAutoVisible(false), CALLOUT_VISIBLE_MS);
    }, CALLOUT_SHOW_DELAY_MS);
    return () => {
      clearTimeout(showTimer);
      if (hideTimer) clearTimeout(hideTimer);
    };
  }, [bubbleOpen]);

  // Opening the chat counts as having used the session's call-out, so closing it
  // later does not bring the speech bubble back a few seconds after they chatted.
  useEffect(() => {
    if (bubbleOpen) markCalloutSeen();
  }, [bubbleOpen]);

  // A reply finished while closed: hop once and say so, even if the session
  // call-out was already used. Compared against the previous render's count, so a
  // count already there at mount is not news. Adjusting state while rendering is
  // React's pattern for "state that follows a prop change".
  const [seenUnread, setSeenUnread] = useState(unreadCount);
  if (seenUnread !== unreadCount) {
    setSeenUnread(unreadCount);
    if (unreadCount > seenUnread && !bubbleOpen) {
      setUnreadNote(true);
      setHop(true);
    }
  }
  // Opening the chat answers the call-out: clear everything it was saying.
  if (bubbleOpen && (autoVisible || hover || focusWithin || unreadNote || hop)) {
    setAutoVisible(false);
    setHover(false);
    setFocusWithin(false);
    setUnreadNote(false);
    setHop(false);
  }

  const calloutVisible = autoVisible || hover || focusWithin || unreadNote;
  return {
    calloutId,
    calloutVisible,
    /** Only the call-outs that arrive on their own are announced. The hover/focus one is
     *  something the person asked for, so it is a description of the badger. */
    live: autoVisible || unreadNote,
    /** The unread note replaces the page line; the component picks the words (badgerCallout). */
    unreadNote,
    wave: autoVisible && !unreadNote && !hover && !waveDone && !prefersReducedMotion(),
    hop,
    setHover,
    onFocusWithin: () => setFocusWithin(true),
    onBlurWithin: (event: FocusEvent<HTMLElement>) => {
      // Focus moving between the launcher and the call-out stays "within".
      const next = event.relatedTarget as Node | null;
      if (next && event.currentTarget.contains(next)) return;
      setFocusWithin(false);
    },
    dismiss: () => {
      markCalloutSeen();
      setAutoVisible(false);
      setUnreadNote(false);
      setHover(false);
      setFocusWithin(false);
    },
    onMascotAnimationEnd: () => {
      setHop(false);
      setWaveDone(true);
    },
  };
}
